<template>
  <DefaultLayout>
    <div slot="content">

      <div class="d-flex align-center py-3">
        <div class="title py-3">Subscription Plans</div>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" @click="newPlan()">Add new</v-btn>
      </div>
      <v-divider></v-divider>

      <v-data-table :headers="plansHeaders" :loading="dataLoading" :items="plans" :search="search" 
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, { text: 'All', value: -1 }],
          showFirstLastPage: false,
          prevIcon: 'fal fa-chevron-left',
          nextIcon: 'fal fa-chevron-right',
          itemsPerPageText: 'Items per page'
        }"
        :hide-default-footer="plans.length < 11">
        <template v-slot:top="{ pagination, options, updateOptions }">
          <v-data-footer 
            :pagination="pagination" 
            :options="options"
            :itemsPerPageOptions="[10, 20, 50, { text: 'All', value: -1 }]"
            :showFirstLastPage="false"
            prevIcon="fal fa-chevron-left"
            nextIcon="fal fa-chevron-right"
            @update:options="updateOptions"
            items-per-page-text="Items per page" />
        </template>
        <template v-slot:item.id="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" text icon color="blue" @click="view(item)"><v-icon>fa-solid fa-eye</v-icon></v-btn>
            </template>
            <span>Edit plan</span>
          </v-tooltip>
        </template>
        <template v-slot:item.dateCreated="{ item }">
          <span>{{ getDate(item.dateCreated) }}</span>
        </template>
        <template v-slot:item.dateUpdated="{ item }">
          <span>{{ getDate(item.dateUpdated) }}</span>
        </template>
        <template v-slot:item.privatePlan="{ item }">
          <span>{{ getYesNo(item.privatePlan) }}</span>
        </template>
      </v-data-table>

    <v-dialog v-model="editDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary" >
          <v-btn icon dark  @click="editDialog = false" >
            <v-icon>fa-thin fa-circle-xmark</v-icon>
          </v-btn>
          <v-toolbar-title>Subscription Plan</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn v-if="editable" dark text @click="savePlan" >
              Save
            </v-btn>
            <v-btn v-if="!editable" dark text @click="editPlan" >
              Edit
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-list three-line subheader >
          <v-form ref="planForm">
            <v-subheader>General</v-subheader>
            <v-list-item>
              <v-list-item-action class="mr-5">
                <v-text-field :disabled="!editable" dense v-model="name" :rules="rules" label="Name"></v-text-field>
              </v-list-item-action>
              <v-list-item-action class="mr-5">
                <v-text-field :disabled="!editable" dense v-model="pricePerMonth" :rules="rules" label="Price Per Month"></v-text-field>
              </v-list-item-action>
              <v-list-item-action class="mr-5">
                <v-text-field :disabled="!editable" dense v-model="daysTrialPeriod" :rules="rules" label="Days Trial Period"></v-text-field>
              </v-list-item-action>
              <v-list-item-action class="mr-5">
                <v-text-field :disabled="!editable" dense v-model="annualDiscount" :rules="rules" label="Annual Pay Discount Percentage"></v-text-field>
              </v-list-item-action>
              <v-list-item-action class="mr-5">
                <v-switch :disabled="!editable" v-model="privatePlan" inset label="Private Plan"></v-switch>
              </v-list-item-action>
            </v-list-item>
            <v-divider></v-divider>
            <v-subheader>Feature list</v-subheader>
            <v-row class="align-top pt-3 pb-4">
              <v-col v-for="s in feature" cols="2" sm="2">
                <v-list-item class="mt-0 mb-0" v-for="f in s" :key="f.type" style="margin: 1px !important; min-height: 5px !important; ">
                  <v-list-item-action style="margin: 1px !important; min-height: 5px !important; ">
                    <v-checkbox :disabled="!editable" v-model="f.enabled" inset :label="f.name" ></v-checkbox>
                  </v-list-item-action>
                </v-list-item>
              </v-col>
            </v-row>
            <v-subheader>Limitations</v-subheader>
            <v-row class="align-top pt-3 pb-4">
              <v-col v-for="s in featureQuota" cols="2" sm="2">
                <v-list-item class="mt-0 mb-0" v-for="f in s" :key="f.type" style="margin: 1px !important; min-height: 5px !important; ">
                  <v-list-item-action style="margin: 10px !important; min-height: 15px !important; ">
                    <v-text-field :disabled="!editable" v-model="f.quota" dense :label="f.name" class="mb-2"></v-text-field>
                  </v-list-item-action>
                  <v-list-item-action style="margin: 10px !important; min-height: 15px !important; ">
                    <v-checkbox :disabled="!editable" v-model="f.burnNotUse" inset label="Burn not used"></v-checkbox>
                  </v-list-item-action>
                </v-list-item>
              </v-col>
            </v-row>
          </v-form>
        </v-list>
      </v-card>
    </v-dialog>

    </div>
  </DefaultLayout>
</template>

<script>
import uuidv4 from 'uuid/v4';
import { GET_SUBSCRIPTION_PLANS, GET_SUBSCRIPTION_PLAN_FEATURE, UPDATE_SUBSCRIPTION_PLAN } from '@/store/actions.type';
import DefaultLayout from '@/layouts/DefaultLayout';

export default {
  name: 'sms-templates',
  components: {
    DefaultLayout,
  },
  data() {
    return {
      dataLoading: false,
      plans: [],
      search: '',
      plansHeaders: [
        { text: 'Name', value: 'name', align: 'left' },
        { text: 'Created', value: 'dateCreated', align: 'left' },
        { text: 'Updated', value: 'dateUpdated', align: 'left' },
        { text: 'Price per month', value: 'pricePerMonth', align: 'left' },
        { text: 'Trial period (days)', value: 'daysTrialPeriod', align: 'left' },
        { text: 'Private plan', value: 'privatePlan', align: 'left' },
        { text: 'Actions', value: 'id', align: 'left', sortable: false },
      ],
      editDialog: false,
      privatePlan: false,
      feature: [],
      featureQuota: [],
      name: '',
      pricePerMonth: 0,
      daysTrialPeriod: 0,
      annualDiscount: 0,
      rules: [
        value => !!value || 'Required.'
      ],
      edit: false,
      editable: false,
      id: null,
      rawFeature: [],
      rawQuota: [],
      editable: false
    };
  },
  computed: {

  },
  methods: {
    async savePlan(){
      if (!this.$refs.planForm.validate()) {
        return;
      }

      if(!this.edit){
        this.id = uuidv4();
      }

      let features = [];
      this.feature.forEach(ar => {
        ar.forEach(el => {
          if(el.type){
            features.push({ feature: el.type, enabled: el.enabled });
          }else{
            features.push({ feature: el.feature, enabled: el.enabled });
          }
        });
      });

      let featureQuota = [];
      this.featureQuota.forEach(ar => {
        ar.forEach(el => {
          if(el.type){
            featureQuota.push({ feature: el.type, quota: el.quota, burnNotUse: el.burnNotUse });
          }else{
            featureQuota.push({ feature: el.feature, quota: el.quota, burnNotUse: el.burnNotUse });
          }
        });
      });

      const plan = {
        id: this.id,
        name: this.name,
        pricePerMonth: this.pricePerMonth,
        privatePlan: this.privatePlan,
        daysTrialPeriod: this.daysTrialPeriod,
        annualPayDiscountPercentage: this.annualDiscount,
        features: features,
        featureQuotas: featureQuota
      }

      try {
        await this.$store.dispatch(UPDATE_SUBSCRIPTION_PLAN, plan);
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Updated!',
        });
        this.profileTemplateDialog = false;
        await this.getSubscriptionPlans();
      } catch(err) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Error creating template. Please contact support@planetverify.com',
        });
      }

      this.editDialog = false;
    },
    async getSubscriptionPlans() {
      this.dataLoading = true;
      const response = await this.$store.dispatch(GET_SUBSCRIPTION_PLANS);
      this.plans = response.list;
      this.dataLoading = false;
    },
    async getSubscriptionPlanFeature() {
      this.feature = [];
      this.featureQuota = [];
      const response = await this.$store.dispatch(GET_SUBSCRIPTION_PLAN_FEATURE);
      this.rawFeature = response.feature;
      this.rawQuota = response.featureQuota;
      response.feature.forEach(el => {
        el.enabled = false
      });
      response.featureQuota.forEach(el => {
        el.quota = 0,
        el.burnNotUse = false
      });
      this.features = response;

      let chunkSize = 10;
      for (let i = 0; i < response.feature.length; i += chunkSize) {
          const chunk = response.feature.slice(i, i + chunkSize);
          this.feature.push(chunk);
      }

      chunkSize = 1;
      for (let i = 0; i < response.featureQuota.length; i += chunkSize) {
          const chunk = response.featureQuota.slice(i, i + chunkSize);
          this.featureQuota.push(chunk);
      }
    },
    getDate(dateTime) {
      return new Date(dateTime).toLocaleString('en-GB', { timeZone: 'UTC' });
    },
    getYesNo(boolField){
      if(boolField){
        return 'YES'
      }
      return 'NO'
    },
    fillFeatures(list){
      list.forEach(el => {
        let f = this.rawFeature.find(x => x.type === el.feature);
        if(f){
          el.name = f.name;
        }
      });
      this.feature = [];
      let chunkSize = 10;
      for (let i = 0; i < list.length; i += chunkSize) {
          const chunk = list.slice(i, i + chunkSize);
          this.feature.push(chunk);
      }
    },
    fillQuotas(list){
      list.forEach(el => {
        if(this.rawQuota.find(x => x.type === el.feature)){
          el.name = this.rawQuota.find(x => x.type === el.feature).name;
        }
      });
      this.featureQuota = [];
      let chunkSize = 1;
      for (let i = 0; i < list.length; i += chunkSize) {
          const chunk = list.slice(i, i + chunkSize);
          this.featureQuota.push(chunk);
      }
    },
    view(plan){
      this.edit = true;
      this.editable = false;
      this.id = plan.id;
      this.name = plan.name;
      this.pricePerMonth = plan.pricePerMonth;
      this.privatePlan = plan.privatePlan;
      this.daysTrialPeriod = plan.daysTrialPeriod;
      this.annualDiscount = plan.annualPayDiscountPercentage;
      this.pricePerMonth = plan.pricePerMonth;
      this.fillFeatures(plan.features);
      this.fillQuotas(plan.featureQuotas);
      this.editDialog = true;
    },
    async newPlan(){
      await this.getSubscriptionPlanFeature();
      this.editable = true;
      this.edit = false;
      this.name = '';
      this.pricePerMonth = 0;
      this.privatePlan = false;
      this.daysTrialPeriod = 0;
      this.annualDiscount = 0;
      this.pricePerMonth = 0;
      this.editDialog = true;
    },
    editPlan(){
      this.editable = true;
    }
  },
  async mounted() {
    await this.getSubscriptionPlanFeature();
    await this.getSubscriptionPlans();
  },
}
</script>
